<script setup lang="ts">
import { getDirectusAssetURL } from '@@/server/utils/directus-utils';
import { Play, X } from 'lucide-vue-next';

interface HeroProps {
	data: {
		id: string;
		tagline: string;
		headline: string;
		description: string;
		content?: string | null;
		layout: 'image_image_left' | 'image_center' | 'image_left' | 'image_background';
		image: string;
		video?: string | { id: string; type?: string | null; title?: string | null } | null;
		video_preview_image?: string | { id: string } | null;
		text_alignment?: 'left' | 'center' | 'right' | null;
		block_height_percentage?: number | null;
		button_group?: {
			id?: string;
			buttons?: Array<{
				id: string;
				label: string | null;
				variant: string | null;
				url: string | null;
				type: 'url' | 'page' | 'post' | 'page-block';
				pagePermalink?: string | null;
				postSlug?: string | null;
				page?: { permalink: string | null };
				post?: { slug: string | null };
				page_block?: { id: string | number } | string | null;
			}>;
		};
	};
	background?: 'light' | 'dark' | null;
}

const { setAttr } = useVisualEditing();
const props = defineProps<HeroProps>();

const isVideoModalOpen = ref(false);

// Helper function per ottenere l'ID del file video
function getVideoId(video: string | { id: string; type?: string | null; title?: string | null } | null | undefined): string | null {
	if (!video) {
		return null;
	}
	return typeof video === 'string' ? video : video.id;
}

// Helper function per ottenere il title del video
function getVideoTitle(video: string | { id: string; type?: string | null; title?: string | null } | null | undefined): string | null {
	if (!video || typeof video === 'string') {
		return null;
	}
	return video.title || null;
}

// Helper function per ottenere l'ID dell'immagine di anteprima
function getPreviewImageId(previewImage: string | { id: string } | null | undefined): string | null {
	if (!previewImage) {
		return null;
	}
	return typeof previewImage === 'string' ? previewImage : previewImage.id;
}

// Determina se ci sono video e preview image popolati
const hasVideo = computed(() => {
	return !!(props.data.video && props.data.video_preview_image);
});

function handleOpenVideoModal() {
	if (hasVideo.value) {
		isVideoModalOpen.value = true;
	}
}

function handleKeyDown(e: KeyboardEvent) {
	if (!isVideoModalOpen.value) return;
	if (e.key === 'Escape') {
		isVideoModalOpen.value = false;
	}
}

onMounted(() => {
	window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeyDown);
});

const backgroundImageUrl = computed(() => {
	if (props.data.layout === 'image_background' && props.data.image) {
		return getDirectusAssetURL(props.data.image);
	}
	return null;
});

const textColor = computed(() => {
	// Se il layout è image_background E c'è un'immagine di sfondo, il testo è bianco
	if (props.data.layout === 'image_background' && backgroundImageUrl.value) {
		return 'text-white';
	}
	// Se il background è dark, il testo è bianco
	if (props.background === 'dark') {
		return 'text-white';
	}
	// Se il background è light o null, il testo è nero (sfondo bianco)
	return 'text-black';
});

const textAlignmentClasses = computed(() => {
	if (!props.data.text_alignment) {
		// Se text_alignment non è definito, usa il comportamento di default basato sul layout
		return {};
	}
	
	const alignment = props.data.text_alignment;
	return {
		'text-left items-start': alignment === 'left',
		'text-center items-center': alignment === 'center',
		'text-left md:text-right items-start md:items-end': alignment === 'right',
	};
});

const buttonAlignmentClasses = computed(() => {
	if (!props.data.text_alignment) {
		// Se text_alignment non è definito, usa il comportamento di default
		return {
			'flex justify-center': props.data.layout === 'image_center',
		};
	}
	
	const alignment = props.data.text_alignment;
	return {
		'flex justify-start': alignment === 'left',
		'flex justify-center': alignment === 'center',
		'flex justify-start md:justify-end': alignment === 'right',
	};
});

const descriptionWrapperClasses = computed(() => {
	const baseClasses = 'w-full';
	if (!props.data.text_alignment || props.data.text_alignment === 'left') {
		return `${baseClasses} [&_.prose]:text-left [&_p]:text-left [&_ul]:text-left [&_ol]:text-left [&_li]:text-left`;
	}
	if (props.data.text_alignment === 'center') {
		return `${baseClasses} [&_.prose]:text-center [&_p]:text-center [&_ul]:text-center [&_ol]:text-center [&_li]:text-center`;
	}
	// Su mobile forza a sinistra, su desktop mantiene a destra
	return `${baseClasses} [&_.prose]:text-left md:[&_.prose]:text-right [&_p]:text-left md:[&_p]:text-right [&_ul]:text-left md:[&_ul]:text-right [&_ol]:text-left md:[&_ol]:text-right [&_li]:text-left md:[&_li]:text-right`;
});

// Calcola l'altezza per il layout image_background
const heroHeight = computed(() => {
	if (props.data.layout === 'image_background' && backgroundImageUrl.value) {
		// Usa block_height_percentage se disponibile, altrimenti default 80
		const heightPercentage = props.data.block_height_percentage ?? 80;
		return `${heightPercentage}vh`;
	}
	return null;
});
</script>

<template>
	<section
		class="relative flex gap-6 md:gap-12 max-w-full"
		:class="{
			'flex-col-reverse w-full mx-auto': data.layout !== 'image_background' && data.image && !hasVideo,
			'flex-col w-full mx-auto': data.layout !== 'image_background' && (hasVideo || !data.image),
			'flex-col w-full justify-center': data.layout === 'image_background',
			'items-center text-center': data.layout === 'image_center' && !data.text_alignment,
			'md:flex-row-reverse': data.layout === 'image_left',
			'md:flex-row': data.layout !== 'image_center' && data.layout !== 'image_left' && data.layout !== 'image_background',
			'items-start': data.layout === 'image_background' && (!data.text_alignment || data.text_alignment === 'left'),
			'items-center': (data.layout === 'image_background' && data.text_alignment === 'center') || (data.layout !== 'image_background' && !data.text_alignment && data.layout !== 'image_center'),
			'items-start md:items-end': data.layout === 'image_background' && data.text_alignment === 'right',
			'bg-white py-12 md:py-16 lg:py-20': data.layout === 'image_background' && !backgroundImageUrl,
		}"
		:style="{
			...(backgroundImageUrl ? {
				backgroundImage: `url(${backgroundImageUrl})`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				backgroundRepeat: 'no-repeat',
				...(background === 'dark' ? { backgroundColor: '#000000' } : {}),
			} : {}),
			...(heroHeight ? { height: heroHeight } : {}),
		}"
	>
		<!-- Overlay per migliorare la leggibilità del testo quando c'è un background -->
		<div
			v-if="data.layout === 'image_background' && backgroundImageUrl"
			class="absolute inset-0 bg-black/5 z-0"
		/>
		
		<div
			class="flex flex-col gap-4 relative z-10"
			:class="{
				// Width e padding basati sul layout
				'w-full md:w-3/4 xl:w-2/3 mx-auto': data.layout === 'image_center',
				'w-full md:w-1/2': data.layout !== 'image_center' && data.layout !== 'image_background',
				'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-16': data.layout === 'image_background',
				// Allineamento di default solo se text_alignment non è definito
				'items-center': data.layout === 'image_center' && !data.text_alignment,
				'items-start': (data.layout !== 'image_center' && data.layout !== 'image_background' && !data.text_alignment) || (data.layout === 'image_background' && !data.text_alignment),
				// Su mobile forza items-start anche se text_alignment è right
				'md:items-end': data.text_alignment === 'right' && data.layout === 'image_background',
				// Colore del testo gestito da textColor computed
				'text-white': (data.layout === 'image_background' && data.image) || background === 'dark',
				'text-black': (background === 'light' || !background) && !(data.layout === 'image_background' && data.image),
				// Allineamento basato su text_alignment (sovrascrive il default)
				...textAlignmentClasses,
			}"
		>
			<Tagline
				:tagline="data.tagline"
				:className="textColor"
				:data-directus="setAttr({ collection: 'block_hero', item: data.id, fields: 'tagline', mode: 'popover' })"
			/>
			<Headline
				:headline="data.headline"
				:text-color="textColor"
				:data-directus="setAttr({ collection: 'block_hero', item: data.id, fields: 'headline', mode: 'popover' })"
			/>
			<div
				v-if="data.description"
				:class="descriptionWrapperClasses"
			>
				<Text
					:content="data.description"
					:text-color="textColor"
					class="max-w-none w-full"
					:data-directus="setAttr({ collection: 'block_hero', item: data.id, fields: 'description', mode: 'popover' })"
				/>
			</div>
			<div
				v-if="data.content"
				:class="descriptionWrapperClasses"
			>
				<Text
					:content="data.content"
					:text-color="textColor"
					size="base"
					class="max-w-none w-full prose-base [&_p]:!mb-0 [&_p+_p]:!mt-2 [&_ul]:!mb-0 [&_ul]:!mt-2 [&_ul]:!pl-0 [&_li]:!pl-0 prose-p:text-base prose-ul:text-base prose-li:text-base"
					:data-directus="setAttr({ collection: 'block_hero', item: data.id, fields: 'content', mode: 'drawer' })"
				/>
			</div>

			<div
				v-if="data.button_group?.buttons?.length"
				:class="buttonAlignmentClasses"
			>
				<ButtonGroup
					:buttons="data.button_group.buttons"
					:data-directus="
						setAttr({ collection: 'block_button_group', item: data.button_group?.id || null, fields: 'buttons', mode: 'modal' })
					"
				/>
			</div>
		</div>

		<div
			v-if="(data.image || hasVideo) && data.layout !== 'image_background'"
			class="relative w-full flex items-start md:items-center"
			:class="{
				'md:w-3/4 xl:w-2/3 md:h-[400px]': data.layout === 'image_center',
				'md:w-1/2': data.layout !== 'image_center',
				'md:h-[562px]': data.layout !== 'image_center' && !hasVideo,
				'cursor-pointer': hasVideo,
			}"
			:style="hasVideo && data.layout !== 'image_center' ? { height: 'auto' } : {}"
			@click="hasVideo ? handleOpenVideoModal() : null"
		>
			<!-- Video preview con immagine di anteprima e pulsante play -->
			<template v-if="hasVideo">
				<div
					class="relative w-full group overflow-hidden"
					:class="{
						'ml-0 md:mr-0 md:ml-auto': data.layout === 'image_left',
						'ml-0': data.layout !== 'image_left' && data.layout !== 'image_center',
						'mx-auto': data.layout === 'image_center',
					}"
					style="aspect-ratio: 2/3; max-width: 400px; width: 100%; position: relative;"
					:data-directus="
						setAttr({ collection: 'block_hero', item: data.id, fields: ['video', 'video_preview_image', 'layout'], mode: 'modal' })
					"
				>
					<!-- Immagine di anteprima come sfondo -->
					<DirectusImage
						v-if="getPreviewImageId(data.video_preview_image)"
						:uuid="getPreviewImageId(data.video_preview_image)!"
						:alt="getVideoTitle(data.video) || data.tagline || data.headline || 'Video preview'"
						:fill="true"
						:sizes="data.layout === 'image_center' ? '100vw' : '(max-width: 768px) 100vw, 50vw'"
						class="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
					/>
					<!-- Fallback se non c'è immagine di anteprima -->
					<div
						v-else
						class="w-full h-full bg-gray-900"
					/>
					
					<!-- Pulsante play centrato -->
					<div class="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors duration-300">
						<button
							@click.stop="handleOpenVideoModal"
							class="group/play"
							aria-label="Play video"
						>
							<div class="bg-white/90 rounded-full p-3 md:p-4 group-hover/play:bg-white group-hover/play:scale-110 transition-all duration-300">
								<Play class="w-6 h-6 md:w-8 md:h-8 text-accent" fill="currentColor" />
							</div>
						</button>
					</div>
				</div>
			</template>
			
		<!-- Immagine normale -->
		<template v-else-if="data.image">
			<div class="relative w-full md:h-full">
				<!-- Su mobile: immagine con dimensioni naturali (no fill) -->
				<DirectusImage
					:uuid="data.image"
					:alt="data.tagline || data.headline || 'Hero Image'"
					:sizes="data.layout === 'image_center' ? '100vw' : '(max-width: 768px) 100vw, 50vw'"
					class="object-contain w-full h-auto md:hidden"
					:data-directus="
						setAttr({ collection: 'block_hero', item: data.id, fields: ['image', 'layout'], mode: 'modal' })
					"
				/>
				<!-- Su desktop: immagine con fill -->
				<DirectusImage
					:uuid="data.image"
					:alt="data.tagline || data.headline || 'Hero Image'"
					:fill="true"
					:sizes="data.layout === 'image_center' ? '100vw' : '(max-width: 768px) 100vw, 50vw'"
					class="object-contain hidden md:block"
					:data-directus="
						setAttr({ collection: 'block_hero', item: data.id, fields: ['image', 'layout'], mode: 'modal' })
					"
				/>
			</div>
		</template>
		</div>

		<!-- Dialog per il video -->
		<Dialog v-model:open="isVideoModalOpen">
			<DialogContent
				class="flex max-w-full max-h-full items-center justify-center p-2 bg-transparent border-none z-50"
				hideCloseButton
			>
				<DialogTitle class="sr-only">Hero Video</DialogTitle>
				<DialogDescription class="sr-only">
					Playing video: {{ getVideoTitle(data.video) || data.headline || 'Hero video' }}
				</DialogDescription>
				<DialogHeader />

				<div class="relative w-[90vw] h-[90vh] flex items-center justify-center">
					<div
						v-if="hasVideo"
						class="flex items-center justify-center"
						style="aspect-ratio: 2/3; width: min(calc(90vh * 2 / 3), 90vw); height: min(90vh, calc(90vw * 3 / 2));"
					>
						<DirectusVideo
							:uuid="getVideoId(data.video)!"
							:title="getVideoTitle(data.video)"
							:show-title="false"
							:autoplay="true"
							class="w-full h-full object-contain"
						/>
					</div>
				</div>

				<DialogClose asChild>
					<button
						class="absolute top-4 right-4 bg-black bg-opacity-70 text-white rounded-full p-2 hover:bg-opacity-90"
						aria-label="Close Video"
					>
						<X class="w-8 h-8" />
					</button>
				</DialogClose>
			</DialogContent>
		</Dialog>
	</section>
</template>
